import React, { useState, useEffect } from "react";
import { Table, Input, Button, Card, Tooltip, Spin } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { tiposContratosApi } from "../../services/api";
import TipoContratoFormModal from "./TipoContratoFormModal";
import DeleteConfirmModal from "../DeleteConfirmModal";

const TablaTiposContratos = () => {
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTipoContrato, setEditingTipoContrato] = useState(null);
  const [tiposContratos, setTiposContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [tipoContratoToDelete, setTipoContratoToDelete] = useState(null);

  // Cargar datos al iniciar el componente
  useEffect(() => {
    fetchTiposContratos()
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error cargando datos iniciales:", error);
        toast.error("Error al cargar los datos", {
          position: "top-right",
          autoClose: 4000,
        });
        setLoading(false);
      });
  }, []);

  // Función para obtener tipos de contratos
  const fetchTiposContratos = async () => {
    try {
      const response = await tiposContratosApi.getAll();
      if (response.data.status) {
        const tiposContratosData = response.data.tipos_contratos.map((item) => ({
          key: item.id.toString(),
          id: item.id,
          tipo_contrato: item.tipo_contrato,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }));
        setTiposContratos(tiposContratosData);
      }
    } catch (error) {
      console.error("Error obteniendo tipos de contratos:", error);
      toast.error("Error al cargar los tipos de contratos", {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90, marginRight: 8 }}
          className="bg-blue-600 hover:bg-blue-700"
        >
          Buscar
        </Button>
        <Button
          onClick={() => clearFilters()}
          size="small"
          style={{ width: 90 }}
        >
          Limpiar
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
  });

  const columns = [
    {
      title: <div className="font-medium">ID</div>,
      dataIndex: "id",
      key: "id",
      width: "10%",
    },
    {
      title: <div className="font-medium">Tipo de Contrato</div>,
      dataIndex: "tipo_contrato",
      key: "tipo_contrato",
      ...getColumnSearchProps("tipo_contrato"),
      width: "70%",
    },
    {
      title: <div className="font-medium text-center">Acciones</div>,
      key: "acciones",
      align: "center",
      render: (_, record) => (
        <div className="flex gap-2 justify-center">
          <Tooltip title="Editar tipo de contrato">
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              type="primary"
              size="middle"
              className="bg-blue-500 hover:bg-blue-700"
            />
          </Tooltip>
          <Tooltip title="Eliminar tipo de contrato">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              type="primary"
              danger
              size="middle"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleReset = () => {
    setSearchText("");
    fetchTiposContratos();
  };

  const showModal = () => {
    setIsModalVisible(true);
    setEditingTipoContrato(null);
  };

  const handleEdit = (tipoContrato) => {
    setEditingTipoContrato(tipoContrato);
    setIsModalVisible(true);
  };

  const handleDelete = (tipoContrato) => {
    setTipoContratoToDelete(tipoContrato);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    try {
      await tiposContratosApi.delete(tipoContratoToDelete.id);
      toast.success(`Tipo de contrato "${tipoContratoToDelete.tipo_contrato}" eliminado correctamente`, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setDeleteModalVisible(false);
      setTipoContratoToDelete(null);
      fetchTiposContratos(); // Recargar la lista de tipos de contratos
    } catch (error) {
      console.error("Error eliminando tipo de contrato:", error);
      let errorMsg = "Error al eliminar el tipo de contrato";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMsg = error.response.data.message;
      }
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const handleModalSuccess = () => {
    setIsModalVisible(false);
    setEditingTipoContrato(null);
    fetchTiposContratos(); // Recargar la lista después de un cambio exitoso
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
        <Spin size="large" tip="Cargando tipos de contratos..." />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-gray-800">
              Administración de Tipos de Contratos
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showModal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Nuevo Tipo de Contrato
            </Button>
          </div>
        }
        className="shadow-md"
      >
        <div className="flex justify-between items-center mb-6">
          <div>
            <Input
              placeholder="Buscar tipo de contrato..."
              className="w-72"
              prefix={<SearchOutlined className="text-gray-400" />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="large"
            />
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            type="default"
            className="h-10"
          >
            Reiniciar filtros
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={tiposContratos.filter(
            (item) =>
              item.tipo_contrato.toLowerCase().includes(searchText.toLowerCase())
          )}
          rowClassName={() =>
            "hover:bg-blue-50 transition-colors cursor-pointer"
          }
          scroll={{ y: "calc(100vh - 350px)" }}
          pagination={{
            total: tiposContratos.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} tipos de contrato`,
          }}
          className="border border-gray-200 rounded-md"
        />

        {/* Modal para crear/editar tipo de contrato */}
        <TipoContratoFormModal
          visible={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          onSuccess={handleModalSuccess}
          editingTipoContrato={editingTipoContrato}
        />

        {/* Modal de confirmación para eliminar tipo de contrato */}
        <DeleteConfirmModal
          visible={deleteModalVisible}
          onCancel={() => setDeleteModalVisible(false)}
          onConfirm={confirmDelete}
          title="Eliminar Tipo de Contrato"
          itemDescription="el tipo de contrato"
          itemName={tipoContratoToDelete ? tipoContratoToDelete.tipo_contrato : ""}
        />
      </Card>
    </div>
  );
};

export default TablaTiposContratos;